import type { CampaignRecommendationMode } from "./campaignConfig";

export const CAMPAIGN_TYPE_IDS = {
  AFTER_ADD_PRODUCTS: "after_add_products",
  PRODUCT_PAGE_ADDONS: "product_page_addons",
  PRE_CHECKOUT_UPSELL: "pre_checkout_upsell",
} as const;

export type CampaignType = (typeof CAMPAIGN_TYPE_IDS)[keyof typeof CAMPAIGN_TYPE_IDS];
export type CampaignTrigger = "after_add" | "product_page" | "pre_checkout";
export type CampaignPlacement = "product_page" | "cart";
export type CampaignPresentation = "popup" | "inline_block";

export type CampaignTypeDefinition = {
  id: CampaignType;
  title: string;
  description: string;
  editorDescription: string;
  trigger: CampaignTrigger;
  placement: CampaignPlacement;
  presentation: CampaignPresentation;
  recommendationMode: CampaignRecommendationMode;
  placementLabel: string;
  presentationLabel: string;
  defaultName: string;
  defaultCondition: "product" | "collection";
  defaultLayout: "products_first" | "services_first";
  supportsServices: boolean;
};

export const CAMPAIGN_TYPES: CampaignTypeDefinition[] = [
  {
    id: CAMPAIGN_TYPE_IDS.AFTER_ADD_PRODUCTS,
    title: "After a matching product is added",
    description: "Show a product-only bundle immediately after a selected product, collection, or tag is added to cart.",
    editorDescription: "Choose the triggering products and the related products customers can add as a complete or custom bundle.",
    trigger: "after_add",
    placement: "product_page",
    presentation: "popup",
    recommendationMode: "manual_products",
    placementLabel: "After add to cart",
    presentationLabel: "Product bundle popup",
    defaultName: "After-add product bundle",
    defaultCondition: "collection",
    defaultLayout: "products_first",
    supportsServices: false,
  },
  {
    id: CAMPAIGN_TYPE_IDS.PRODUCT_PAGE_ADDONS,
    title: "Product page add-ons",
    description: "Show selectable upsell and cross-sell products directly beside the product form.",
    editorDescription: "Choose where this inline product-page offer appears and which add-ons customers can select while shopping.",
    trigger: "product_page",
    placement: "product_page",
    presentation: "inline_block",
    recommendationMode: "manual_products",
    placementLabel: "Product page",
    presentationLabel: "Inline add-ons block",
    defaultName: "Product page add-ons",
    defaultCondition: "product",
    defaultLayout: "products_first",
    supportsServices: false,
  },
  {
    id: CAMPAIGN_TYPE_IDS.PRE_CHECKOUT_UPSELL,
    title: "Before checkout",
    description: "Offer optional services and product recommendations before the customer continues to checkout.",
    editorDescription: "Configure cart rules, optional services, product recommendations, copy, and layout.",
    trigger: "pre_checkout",
    placement: "cart",
    presentation: "popup",
    recommendationMode: "manual_products",
    placementLabel: "Before checkout",
    presentationLabel: "Upsell popup",
    defaultName: "Pre-checkout upsell",
    defaultCondition: "product",
    defaultLayout: "services_first",
    supportsServices: true,
  },
];

export function campaignTypeDefinition(campaignType: CampaignType) {
  return CAMPAIGN_TYPES.find((definition) => definition.id === campaignType) ?? CAMPAIGN_TYPES[0];
}

export function campaignTypeFromStored(campaign: { campaignType?: CampaignType; trigger?: CampaignTrigger }): CampaignType {
  if (CAMPAIGN_TYPES.some((definition) => definition.id === campaign.campaignType)) return campaign.campaignType!;
  if (campaign.trigger === "product_page") return CAMPAIGN_TYPE_IDS.PRODUCT_PAGE_ADDONS;
  return campaign.trigger === "after_add" ? CAMPAIGN_TYPE_IDS.AFTER_ADD_PRODUCTS : CAMPAIGN_TYPE_IDS.PRE_CHECKOUT_UPSELL;
}

export function campaignTypeAllowsCondition(campaignType: CampaignType, condition: string) {
  if (campaignType === CAMPAIGN_TYPE_IDS.AFTER_ADD_PRODUCTS) {
    return condition === "product" || condition === "collection" || condition === "tag";
  }
  if (campaignType === CAMPAIGN_TYPE_IDS.PRODUCT_PAGE_ADDONS) {
    return condition === "product" || condition === "collection" || condition === "tag" || condition === "vendor" || condition === "product_type";
  }
  return condition === "product" || condition === "collection" || condition === "tag" || condition === "subtotal_gt" || condition === "subtotal_lt";
}
